import { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "../../../../shared/components/AppLayout";
import EmptyState from "../../../../shared/components/EmptyState";
import GradientShareButton from "../../../../shared/components/GradientShareButton";
import LoadingPage from "../../../../shared/components/LoadingPage";
import UserAvatar from "../../../../shared/components/UserAvatar";
import useBodyScrollLock from "../../../../shared/hooks/useBodyScrollLock";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import {
  buildPlayerProfileShareUrl,
  syncCanonicalShareUrl,
} from "../../../../shared/lib/shareLinks";
import { getPublicPlayerProfile } from "../../shared/services/publicPlayerProfileService";
import {
  PublicPlayerProfile,
  PublicPlayerRankingHighlight,
  PublicPlayerRankingPosition,
} from "../../shared/types/publicPlayerProfile";
import "./PublicPlayerProfilePage.css";

type PublicPlayerProfilePageProps = {
  playerId: number;
};

function formatNumber(value: number) {
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function formatDate(value?: string | null) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(parsed);
}

function buildProfileUrl(playerId: number) {
  return buildPlayerProfileShareUrl(playerId);
}

function formatCategoryBadge(prefix: string, fallback: string, label?: string | null) {
  const safeLabel = String(label || "").trim();
  if (!safeLabel) return fallback;
  const lowerPrefix = prefix.trim().toLowerCase();
  if (safeLabel.toLowerCase().startsWith(lowerPrefix)) return safeLabel;
  return `${prefix} ${safeLabel}`;
}

function formatRankingPosition(
  value: number,
  suffix: string,
  emptyFallback: string,
) {
  const position = Number(value || 0);
  if (!Number.isFinite(position) || position <= 0) return emptyFallback;
  return `${formatNumber(position)}${suffix}`;
}

export default function PublicPlayerProfilePage({
  playerId,
}: PublicPlayerProfilePageProps) {
  const t = siteTexts.publicPlayerProfile;
  const [profile, setProfile] = useState<PublicPlayerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    getPublicPlayerProfile(playerId)
      .then((response) => {
        if (!active) return;
        setProfile(response);
      })
      .catch((err) => {
        if (!active) return;
        setError(err instanceof Error ? err.message : t.loadError);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [playerId, t.loadError]);

  const profileUrl = useMemo(() => buildProfileUrl(playerId), [playerId]);
  const shareTitle = profile
    ? t.shareTitle.replace("{name}", profile.player.displayName)
    : t.title;
  const avatarUrl = String(profile?.player.avatarUrl || "").trim();
  const canOpenPhoto = Boolean(avatarUrl);
  const photoAlt = profile
    ? t.photoAlt.replace("{name}", profile.player.displayName)
    : t.title;

  useBodyScrollLock(photoViewerOpen);

  useEffect(() => {
    syncCanonicalShareUrl(profileUrl);
  }, [profileUrl]);

  useEffect(() => {
    if (!photoViewerOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPhotoViewerOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [photoViewerOpen]);

  const handleShare = useCallback(async () => {
    if (!profile) return;
    const shareText = t.shareText.replace("{name}", profile.player.displayName);
    try {
      if (navigator.share) {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: profileUrl,
        });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(profileUrl);
      }
    } catch (_err) {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(profileUrl);
      }
    }
  }, [profile, profileUrl, shareTitle, t.shareText]);

  if (loading) return <LoadingPage label={siteTexts.common.loading} />;

  if (error || !profile) {
    return (
      <AppLayout>
        <main className="public-player-profile">
          <EmptyState message={error || t.notFound} />
        </main>
      </AppLayout>
    );
  }

  const rankingHighlights = profile.ranking.highlights || [];
  const modalityRankings = profile.ranking.byModality || [];
  const hasRankingResults =
    rankingHighlights.length > 0 || modalityRankings.length > 0;
  const stats = [
    {
      label: t.stats.participations,
      value: profile.stats.participations,
      tone: "blue",
    },
    { label: t.stats.points, value: profile.stats.points, tone: "pink" },
    { label: t.stats.wins, value: profile.stats.wins, tone: "success" },
    { label: t.stats.losses, value: profile.stats.losses, tone: "danger" },
    { label: t.stats.titles, value: profile.stats.titles, tone: "gold" },
    {
      label: t.stats.runnerUps,
      value: profile.stats.runnerUps,
      tone: "purple",
    },
    {
      label: t.stats.gamesBalance,
      value: profile.stats.gamesBalance,
      tone: "open",
    },
  ];
  const categoryCode = String(profile.player.categoryCode || "none")
    .trim()
    .toLowerCase();
  const rankingHighlightLabel = (item: PublicPlayerRankingHighlight) => {
    if (item.key === "own_category") return t.ranking.myCategoryRanking;
    return item.categoryLabel || item.modalityLabel || t.ranking.title;
  };
  const renderRankingCard = (
    item: PublicPlayerRankingPosition,
    label: string,
  ) => (
    <a
      className="public-player-profile__ranking-card"
      href={item.rankingUrl}
      key={`${label}-${item.modalityCode || "all"}-${item.categoryCode || "all"}`}
    >
      <span>{label}</span>
      <strong>
        {formatRankingPosition(
          item.position,
          t.ranking.positionSuffix,
          t.ranking.noPosition,
        )}
      </strong>
      <small>
        {formatNumber(item.points)} {t.ranking.pointsSuffix}
        {item.categoryLabel ? ` · ${item.categoryLabel}` : ""}
      </small>
    </a>
  );

  return (
    <AppLayout>
      <main className="public-player-profile">
        <section className="public-player-profile__hero">
          <button
            type="button"
            className="public-player-profile__avatar-frame"
            onClick={() => {
              if (canOpenPhoto) setPhotoViewerOpen(true);
            }}
            disabled={!canOpenPhoto}
            aria-label={canOpenPhoto ? t.openPhoto : t.photoDialogTitle}
            title={canOpenPhoto ? t.openPhoto : undefined}
          >
            <UserAvatar
              name={profile.player.displayName}
              avatarUrl={avatarUrl}
              size={120}
              className="public-player-profile__avatar"
            />
            {canOpenPhoto ? (
              <span
                className="public-player-profile__avatar-zoom"
                aria-hidden="true"
              >
                zoom_in
              </span>
            ) : null}
          </button>
          <div className="public-player-profile__identity">
            <div className="public-player-profile__title-row">
              <h1>{profile.player.displayName}</h1>
              <span
                className={`public-player-profile__category public-player-profile__category--${categoryCode}`}
              >
                {formatCategoryBadge(
                  t.categoryPrefix,
                  t.categoryFallback,
                  profile.player.categoryLabel,
                )}
              </span>
            </div>
          </div>
          <div className="public-player-profile__actions">
            <GradientShareButton
              className="public-player-profile__share-button"
              onClick={handleShare}
            >
              {t.shareProfile}
            </GradientShareButton>
          </div>
        </section>

        {photoViewerOpen && canOpenPhoto ? (
          <div
            className="public-player-profile__photo-viewer"
            role="dialog"
            aria-modal="true"
            aria-label={t.photoDialogTitle}
            onClick={() => setPhotoViewerOpen(false)}
          >
            <div
              className="public-player-profile__photo-viewer-content"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="public-player-profile__photo-close"
                onClick={() => setPhotoViewerOpen(false)}
              >
                {siteTexts.common.close}
              </button>
              <img src={avatarUrl} alt={photoAlt} />
            </div>
          </div>
        ) : null}

        <section className="public-player-profile__grid">
          <article className="public-player-profile__panel public-player-profile__ranking">
            <div className="public-player-profile__section-header">
              <h2>{t.ranking.title}</h2>
              <span>{t.ranking.subtitle}</span>
            </div>
            {hasRankingResults ? (
              <>
                <div className="public-player-profile__ranking-list">
                  {rankingHighlights.map((item) =>
                    renderRankingCard(item, rankingHighlightLabel(item)),
                  )}
                </div>
                {modalityRankings.length > 0 ? (
                  <div className="public-player-profile__ranking-by-type">
                    <h3>{t.ranking.byTypeTitle}</h3>
                    <div className="public-player-profile__ranking-list public-player-profile__ranking-list--compact">
                      {modalityRankings.map((item) =>
                        renderRankingCard(item, item.modalityLabel),
                      )}
                    </div>
                  </div>
                ) : null}
              </>
            ) : (
              <EmptyState message={t.ranking.empty} />
            )}
          </article>

          <article className="public-player-profile__panel">
            <div className="public-player-profile__section-header">
              <h2>{t.stats.title}</h2>
              <span>{t.stats.subtitle}</span>
            </div>
            <div className="public-player-profile__stats">
              {stats.map((item) => (
                <div
                  className={`public-player-profile__stat public-player-profile__stat--${item.tone}`}
                  key={item.label}
                >
                  <strong>{formatNumber(item.value)}</strong>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </article>
        </section>

        <section className="public-player-profile__panel">
          <div className="public-player-profile__section-header">
            <h2>{t.achievements.title}</h2>
            <span>{t.achievements.subtitle}</span>
          </div>
          {profile.achievements.length > 0 ? (
            <div className="public-player-profile__achievements">
              {profile.achievements.map((achievement) => (
                <article
                  key={achievement.id}
                  className={
                    achievement.rarity === "rare"
                      ? "public-player-profile__achievement--rare"
                      : undefined
                  }
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    {achievement.icon}
                  </span>
                  <div>
                    <h3>{achievement.name}</h3>
                    <p>{achievement.description}</p>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <EmptyState message={t.achievements.empty} />
          )}
        </section>

        <section className="public-player-profile__panel">
          <div className="public-player-profile__section-header">
            <h2>{t.participations.title}</h2>
            <span>{t.participations.subtitle}</span>
          </div>
          {profile.participations.length > 0 ? (
            <div className="public-player-profile__history">
              {profile.participations.map((item) => (
                <article
                  key={`${item.competitionType}-${item.competitionId}-${item.categoryCode || ""}`}
                  className={
                    item.eventImageUrl
                      ? "public-player-profile__history-item--with-image"
                      : undefined
                  }
                >
                  <div className="public-player-profile__history-main">
                    {item.eventImageUrl ? (
                      <span className="public-player-profile__history-image">
                        <img
                          src={item.eventImageUrl}
                          alt={item.competitionName}
                          loading="lazy"
                        />
                      </span>
                    ) : null}
                    <div>
                      {item.detailUrl ? (
                        <a href={item.detailUrl}>{item.competitionName}</a>
                      ) : (
                        <strong>{item.competitionName}</strong>
                      )}
                      <span>
                        {[item.competitionTypeLabel, item.categoryName, formatDate(item.competitionDate)]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </div>
                  </div>
                  <div className="public-player-profile__history-result">
                    <span>
                      {formatNumber(item.pointsEarned)} {t.ranking.pointsSuffix}
                    </span>
                    <strong>{item.resultLabel}</strong>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <EmptyState message={t.participations.empty} />
          )}
        </section>
      </main>
    </AppLayout>
  );
}
